import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { get } from 'lodash';
import {
  examV3EditExportTemplateValidate,
  examV3ExportTemplateOnArrayFieldChange,
  examV3ExportTemplateOnChange,
  examV3ExportTemplatesErrorsSelector,
  examV3ExportTemplatesSelectedTemplateSelector,
  setEditExportTemplateErrors,
} from 'slices/examV3ExportTemplates.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { timeZonesSelector } from 'slices/timeZone.slice';
import { errorNotification } from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';
import { TField, TReservationTemplate, TType } from '@timeedit/types';
import { TEDrawerSection, TEInput } from '@timeedit/ui-components';
import { Collapse, Space } from '@timeedit/ui-components/antd';
import { defaultPageBreak } from '../../../../types/examV3ExportTemplates.type';
import { CommonValueInput, FieldsList } from '../../components';
import './EditExportTemplate.scss';

type Props = {
  capResTemplate: TReservationTemplate;
  sizeResTemplate: TReservationTemplate;
  allTypes: TType[];
  allFields: TField[];
  studentObjectId: number;
  loadingConfiguration: boolean;
};

const EditExportTemplate = (props: Props) => {
  const dispatch = useAppDispatch();
  const selected = useAppSelector(
    examV3ExportTemplatesSelectedTemplateSelector
  );
  const sentErrorMessage = useRef(false);
  const [selectedRows, setSelectedRows] = useState({
    mainHeader: [],
    studentHeader: [],
    studentColumns: [],
    examRangeColumns: [],
  });

  useEffect(() => {
    dispatch(setEditExportTemplateErrors(null));
  }, [dispatch, selected?._id, selected?.id]);

  useEffect(() => {
    if (
      !props.loadingConfiguration &&
      (props.capResTemplate == null || props.sizeResTemplate == null) &&
      !sentErrorMessage.current
    ) {
      if (props.capResTemplate == null) {
        errorNotification('Capacity reservation template is not configured', [
          'Please configure a reservation template for capacity reservations in the Reservation section to the left',
        ]);
      }
      if (props.sizeResTemplate == null) {
        errorNotification('Size reservation template is not configured', [
          'Please configure a reservation template for size reservations in the Reservation section to the left',
        ]);
      }
      sentErrorMessage.current = true;
    }
  }, [props.loadingConfiguration, props.capResTemplate, props.sizeResTemplate]);

  const timezones = useAppSelector(timeZonesSelector);
  const defaultTimezone = timezones.find((zone) => zone.isDefault);
  const validationErrors = useAppSelector(examV3ExportTemplatesErrorsSelector);

  const onChange = useCallback(
    (path: string) => (value: any) => {
      dispatch(
        examV3ExportTemplateOnChange({
          path,
          value,
        })
      );
      if (validationErrors != null) {
        dispatch(examV3EditExportTemplateValidate(false));
      }
    },
    [dispatch, validationErrors]
  );

  const hasError = useCallback(
    (path: string) => {
      return validationErrors?.[path] != null;
    },
    [validationErrors]
  );

  const fieldHasError = useCallback(
    (path: string) =>
      (key: string, index: number, type: string): boolean => {
        if (
          type === 'object-field' &&
          !selected?.[path]?.[index]?.[key]?.includes('-')
        ) {
          return true;
        }
        return validationErrors?.[`${path},${index},${key}`] != null;
      },
    [validationErrors, selected]
  );

  const status = useCallback(
    (path: string): 'error' | undefined => {
      return hasError(path) ? 'error' : undefined;
    },
    [hasError]
  );

  const onRowSelect = useCallback(
    (section: keyof typeof selectedRows) => (rows: any) => {
      setSelectedRows((prev) => ({ ...prev, [section]: rows }));
    },
    []
  );

  const onRowDelete = useCallback(
    (section: keyof typeof selectedRows, dataPath: string) => () => {
      if (!selectedRows[section]?.length) {
        return;
      }

      onChange(dataPath)(
        get(selected, dataPath, []).filter(
          (row) => !selectedRows[section].includes(row)
        )
      );
    },
    [selected, selectedRows, onChange]
  );

  const onFieldChange = useCallback(
    (dataPath: string) => (index: number, key: string, value: any) => {
      dispatch(
        examV3ExportTemplateOnArrayFieldChange({
          path: `${dataPath}[${index}].${key}`,
          value,
        })
      );
      if (validationErrors != null) {
        dispatch(examV3EditExportTemplateValidate(false));
      }
    },
    [dispatch, validationErrors]
  );

  const mainHeaderContent = useMemo(
    () => [
      {
        key: 'mainHeader',
        label: 'Capacity reservation header',
        children: (
          <>
            <FieldsList
              disableWidth
              columnSet="labelAndWidth"
              onRowSelect={onRowSelect('mainHeader')}
              onRowDelete={onRowDelete('mainHeader', 'mainHeader')}
              allFields={props.allFields}
              allTypes={props.allTypes}
              reservationTemplate={props.capResTemplate}
              onAddRow={() => {
                onChange('mainHeader')([
                  ...(selected?.mainHeader ?? []),
                  { id: uuidv4() },
                ]);
              }}
              onChange={onChange('mainHeader')}
              onFieldChange={onFieldChange('mainHeader')}
              values={selected?.mainHeader}
              types={commonValueTypes}
              objectTypeIds={(props.capResTemplate?.types ?? []).map(
                (type) => type.type
              )}
              hasError={fieldHasError('mainHeader')}
            />
            <TEInput.InputNumber
              min={1}
              max={10}
              label="Number of columns"
              value={selected?.mainHeaderNoOfColumns}
              defaultValue={2}
              onChange={onChange('mainHeaderNoOfColumns')}
              status={status('mainHeaderNoOfColumns')}
            />
          </>
        ),
      },
    ],
    [
      onRowSelect,
      onRowDelete,
      onFieldChange,
      props.allFields,
      props.allTypes,
      props.capResTemplate,
      selected,
      fieldHasError,
      status,
    ]
  );

  const examReservationContent = useMemo(
    () => [
      {
        key: 'examReservationHeader',
        label: 'Exam reservation header',
        children: (
          <>
            <FieldsList
              disableWidth
              columnSet="labelAndWidth"
              onRowSelect={onRowSelect('studentHeader')}
              onRowDelete={onRowDelete(
                'studentHeader',
                'seatingList.studentList.header'
              )}
              allFields={props.allFields}
              allTypes={props.allTypes}
              reservationTemplate={props.sizeResTemplate}
              onAddRow={() => {
                onChange('seatingList.studentList.header')([
                  ...(selected?.seatingList?.studentList?.header ?? []),
                  { id: uuidv4() },
                ]);
              }}
              onChange={onChange('seatingList.studentList.header')}
              onFieldChange={onFieldChange('seatingList.studentList.header')}
              values={selected?.seatingList.studentList?.header}
              types={headerTypes}
              objectTypeIds={(props.sizeResTemplate?.types ?? []).map(
                (type) => type.type
              )}
              hasError={fieldHasError('seatingList,studentList,header')}
            />
            <TEInput.InputNumber
              min={1}
              max={10}
              label="Number of columns"
              value={selected?.seatingList.studentList.headerNoOfColumns}
              defaultValue={2}
              onChange={onChange('seatingList.studentList.headerNoOfColumns')}
              status={status('seatingList.studentList.headerNoOfColumns')}
            />
          </>
        ),
      },
    ],
    [
      onChange,
      onRowSelect,
      onRowDelete,
      onFieldChange,
      props.allFields,
      props.allTypes,
      props.sizeResTemplate,
      selected,
      fieldHasError,
      status,
    ]
  );

  const studentColumnsContent = useMemo(
    () => [
      {
        key: 'studentColumns',
        label: 'Student list columns',
        children: (
          <>
            <Space direction="vertical" className="export-tables-half-width">
              <TEInput.Select
                label="Sort by"
                value={selected?.seatingList.studentList.sortBy}
                options={selected?.seatingList.studentList.columns.map(
                  (col) => ({ label: col.label, value: col.id })
                )}
                onChange={onChange('seatingList.studentList.sortBy')}
                status={status('seatingList,studentList,sortBy')}
              />
              <TEInput.Select
                label="Sort order"
                value={selected?.seatingList?.studentList.sort}
                options={[
                  { label: 'Ascending', value: 'asc' },
                  { label: 'Descending', value: 'desc' },
                ]}
                onChange={onChange('seatingList.studentList.sort')}
                status={status('seatingList,studentList,sort')}
              />
            </Space>
            <FieldsList
              columnSet="labelAndWidth"
              onRowSelect={onRowSelect('studentColumns')}
              onRowDelete={onRowDelete(
                'studentColumns',
                'seatingList.studentList.columns'
              )}
              allFields={props.allFields}
              allTypes={props.allTypes}
              reservationTemplate={props.sizeResTemplate}
              onAddRow={() =>
                onChange('seatingList.studentList.columns')([
                  ...(selected?.seatingList?.studentList?.columns ?? []),
                  { id: uuidv4() },
                ])
              }
              onChange={onChange('seatingList.studentList.columns')}
              onFieldChange={onFieldChange('seatingList.studentList.columns')}
              values={selected?.seatingList?.studentList?.columns}
              types={columnTypes}
              objectTypeIds={[
                ...(props.sizeResTemplate?.types ?? []).map(
                  (type) => type.type
                ),
                props.studentObjectId,
              ]}
              hasError={fieldHasError('seatingList,studentList,columns')}
            />
          </>
        ),
      },
    ],
    [
      onChange,
      onFieldChange,
      props.allFields,
      props.allTypes,
      props.sizeResTemplate,
      props.studentObjectId,
      selected,
      fieldHasError,
      status,
      onRowSelect,
      onRowDelete,
    ]
  );

  const examRangeContent = useMemo(
    () => [
      {
        key: 'examRangeColumns',
        label: 'Exam range columns',
        children: (
          <>
            <Space direction="vertical" className="export-tables-half-width">
              <TEInput.Select
                label="Sort by"
                value={selected?.seatingList.examRange?.sortBy}
                options={selected?.seatingList.examRange?.columns?.map(
                  (col) => ({ label: col.label, value: col.id })
                )}
                onChange={onChange('seatingList.examRange.sortBy')}
                status={status('seatingList,examRange,sortBy')}
              />
              <TEInput.Select
                label="Sort order"
                value={selected?.seatingList?.examRange.sort}
                options={[
                  { label: 'Ascending', value: 'asc' },
                  { label: 'Descending', value: 'desc' },
                ]}
                onChange={onChange('seatingList.examRange.sort')}
                status={status('seatingList,examRange,sort')}
              />
            </Space>
            <FieldsList
              columnSet="labelAndWidth"
              onRowSelect={onRowSelect('examRangeColumns')}
              onRowDelete={onRowDelete(
                'examRangeColumns',
                'seatingList.examRange.columns'
              )}
              allFields={props.allFields}
              allTypes={props.allTypes}
              reservationTemplate={props.sizeResTemplate}
              onAddRow={() =>
                onChange('seatingList.examRange.columns')([
                  ...(selected?.seatingList?.examRange?.columns ?? []),
                  { id: uuidv4() },
                ])
              }
              onChange={onChange('seatingList.examRange.columns')}
              onFieldChange={onFieldChange('seatingList.examRange.columns')}
              values={selected?.seatingList?.examRange?.columns}
              types={columnTypes}
              objectTypeIds={(props.sizeResTemplate?.types ?? []).map(
                (type) => type.type
              )}
              hasError={fieldHasError('seatingList,examRange,columns')}
            />
          </>
        ),
      },
    ],
    [
      onChange,
      onFieldChange,
      props.allFields,
      props.allTypes,
      props.sizeResTemplate,
      selected,
      fieldHasError,
      status,
      onRowSelect,
      onRowDelete,
    ]
  );

  if (!selected || !props.capResTemplate || !props.sizeResTemplate) {
    return null;
  }

  return (
    <>
      <TEDrawerSection label="General">
        <Space direction="vertical" className="export-tables-half-width">
          <TEInput.Input
            label="Name"
            tooltip="Name of the export template, shown here and when printing"
            value={selected?.name ?? ''}
            onChange={onChange('name')}
            required
            status={status('name')}
          />
          <TEInput.Input
            label="Title"
            tooltip="The title will be displayed on the top of the first page"
            required
            value={selected?.title ?? ''}
            onChange={onChange('title')}
            status={status('title')}
          />
          <TEInput.TextArea
            label="Description"
            value={selected?.description ?? ''}
            onChange={onChange('description')}
            status={status('description')}
          />
          <TEInput.Select
            label="Timezone"
            allowClear={false}
            value={selected?.timezone ?? defaultTimezone.extId}
            options={timezones.map((timeZone) => ({
              label: timeZone.name,
              value: timeZone.extId,
            }))}
            onChange={onChange('timezone')}
            status={status('timezone')}
          />
          <TEInput.Select
            label="Page orientation"
            value={selected?.pageOrientation}
            defaultValue="portrait"
            onChange={onChange('pageOrientation')}
            options={orientationOptions}
            status={status('pageOrientation')}
          />
          <TEInput.Select
            label="Page break"
            value={selected?.pageBreak}
            defaultValue={defaultPageBreak}
            onChange={onChange('pageBreak')}
            options={pageBreakOptions}
            status={status('pageBreak')}
          />
        </Space>
        <Collapse
          className="export-tables-top-margin"
          defaultActiveKey={['mainHeader']}
          items={mainHeaderContent}
        />
      </TEDrawerSection>
      <TEDrawerSection label="Student seating list">
        <Space direction="vertical" className="export-tables-half-width">
          <TEInput.Select
            label="Title type"
            value={selected?.seatingList.studentList.title.type}
            options={commonValueTypes}
            onChange={(val) => {
              onChange('seatingList.studentList.title.type')(val);
              onChange('seatingList.studentList.title.value')(null);
            }}
            status={status('seatingList,studentList,title,type')}
          />
          <CommonValueInput
            allFields={props.allFields}
            allTypes={props.allTypes}
            reservationTemplate={props.sizeResTemplate}
            type={selected?.seatingList.studentList.title}
            onChange={onChange('seatingList.studentList.title.value')}
            fieldLabel="Title field"
            objectLabel="Title object"
            valueLabel="Text"
            objectTypeIds={(props.sizeResTemplate?.types ?? []).map(
              (type) => type.type
            )}
            hasError={
              hasError('seatingList,studentList,title,value') ||
              (selected.seatingList.studentList?.title?.type ===
                'object-field' &&
                !selected?.seatingList?.studentList?.title?.value?.includes(
                  '-'
                ))
            }
            extraOptions={[
              { label: 'Id', value: 'id' },
              { label: 'Date', value: 'date' },
              { label: 'Time', value: 'time' },
              { label: 'Size', value: 'size' },
            ]}
          />
        </Space>

        <Collapse
          className="export-tables-top-margin"
          defaultActiveKey={['examReservationHeader']}
          items={examReservationContent}
        />

        <Collapse
          className="export-tables-top-margin"
          defaultActiveKey={['studentColumns']}
          items={studentColumnsContent}
        />
      </TEDrawerSection>
      <TEDrawerSection label="Exam Range seating list">
        <Collapse
          className="export-tables-top-margin"
          defaultActiveKey={['examRangeColumns']}
          items={examRangeContent}
        />
      </TEDrawerSection>
    </>
  );
};

const commonValueTypes = [
  { label: 'Object field', value: 'object-field' },
  { label: 'Reservation value', value: 'reservation' },
  { label: 'Export information', value: 'export' },
  { label: 'Plain text value', value: 'text' },
];

const headerTypes = [...commonValueTypes, { label: 'Blank', value: 'blank' }];
const columnTypes = [
  ...headerTypes,
  { label: 'Checkbox', value: 'checkbox' },
  { label: 'Seat', value: 'seat' },
];

const orientationOptions = [
  { label: 'Portrait', value: 'portrait' },
  { label: 'Landscape', value: 'landscape' },
];

const pageBreakOptions = [
  { label: 'Capacity Reservation', value: 'capacity-reservation' },
  { label: 'Exam Reservation', value: 'exam-reservation' },
  {
    label: 'Exam Reservation with header',
    value: 'exam-reservation-with-header',
  },
  { label: 'None', value: 'none' },
];

export default EditExportTemplate;
